import React, { useRef, useMemo } from 'react';
import uploadSvg from 'assets/images/upload.svg';
import uploadCircleSvg from 'assets/images/upload-svg.svg';
import { File } from '../../../../../utils/interfaces/file.interface';
import InputUploadedFile from '../Inputs/InputUploadedFile/InputUploadedFile';

interface Props {
  button?: React.ReactElement | null;
  numberOfFiles: number;
  listFileUpload: File[] | [];
  setListFileUpload: Function;
  style?: any;
  circles?: { label: string; states: string[] };
  insertHtml?: any;
  onDeleteFile?: any;
  loading?: boolean;
  uploadQueueFiles?: any[];
}

const UploadDocs = ({
  button,
  numberOfFiles,
  listFileUpload,
  setListFileUpload,
  style,
  circles,
  insertHtml,
  onDeleteFile,
  loading,
  uploadQueueFiles = []
}: Props) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const allowMultipleFiles = numberOfFiles > 1;

  const enrichedFiles = useMemo(() => {
    const enrichedFromList = listFileUpload.map((file: any) => {
      // Si el archivo ya tiene url o key, es un archivo de BD
      if (file.url || file.key) {
        return file;
      }

      let queueFile;
      if (file.queueId) {
        queueFile = uploadQueueFiles.find((qf: any) => qf.id === file.queueId);
      } else {
        // Fallback: buscar por nombre (para archivos viejos sin queueId)
        queueFile = uploadQueueFiles.find((qf: any) => qf.file.name === file.name);
      }
      if (queueFile) {
        return {
          ...file,
          uploadStatus: queueFile.status,
          errorMessage: queueFile.errorMessage,
          queueId: queueFile.id,
          onRetry: queueFile.onRetry
        };
      }
      return file;
    });

    const errorFilesNotInList = uploadQueueFiles
      .filter((qf: any) => {
        if (qf.status !== 'error') return false;
        
        const isInList = listFileUpload.some((file: any) => 
          file.name === qf.file.name || file.queueId === qf.id
        );
        return !isInList;
      })
      .map((qf: any) => ({
        ...qf.file,
        name: qf.file.name,
        uploadStatus: qf.status,
        errorMessage: qf.errorMessage,
        queueId: qf.id,
        onRetry: qf.onRetry
      }));

    return [...enrichedFromList, ...errorFilesNotInList];
  }, [listFileUpload, uploadQueueFiles]);

  const handleChangeFile = (event: any) => {
    const files = event.target.files;

    if (!files || files.length === 0) return;

    // Calcular cuántos archivos se pueden agregar
    const remainingSlots = numberOfFiles - listFileUpload.length;

    if (remainingSlots <= 0) {
      event.target.value = '';
      return;
    }

    if (allowMultipleFiles) {
      const newFiles = Array.from(files).slice(0, remainingSlots);
      const newList = [...listFileUpload, ...newFiles];
      setListFileUpload(newList);
    } else {
      const newFile = files[0];
      const newList = [...listFileUpload, newFile];
      setListFileUpload(newList);
    }
    event.target.value = '';
  };

  const deleteFile = async (file: any): Promise<void> => {
    if (file?.url || file?.key) await onDeleteFile(file);
    const arrayFiles = [...listFileUpload];
    const deleteIndex = arrayFiles.findIndex((item) => item.name === file.name);
    if (deleteIndex >= 0) {
      arrayFiles.splice(deleteIndex, 1);
      setListFileUpload(arrayFiles);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  
  return (
    <div className="UploadDocs" style={style}>
      {circles && (
        <div className="flex-btw">
          <h4 className="body-card">{circles.label}</h4>
          <div className="circles">
            {circles.states.map((state, index) => (
              <div key={index} className={`circle circle-${state}`} />
            ))}
          </div>
        </div>
      )}

      <div className="contenedor-flex">
        <div className={`felx-btw UploadDocs--container`}>
          {listFileUpload.length < numberOfFiles && (
            <div className="input-content">
              <div className="white" />
              <input
                ref={fileInputRef}
                type="file"
                multiple={allowMultipleFiles}
                onChange={handleChangeFile}
                accept="
                  image/png,
                  image/jpg,
                  image/jpeg,
                  application/pdf,
                  application/vnd.ms-excel,
                  application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
                  .xls,
                  .xlsx
                "
              />

              <div className="empty-input">
                <img src={uploadSvg} alt="upload" className="upload" />
                <p className="body-small body-small-w6">
                  Drag and drop {allowMultipleFiles ? 'files' : 'file'}, or <span className="blue">Browse</span>
                </p>
                <p className="small-detail">Supports png or jpeg files, pdf, xls, xlsx</p>
              </div>
            </div>
          )}
        </div>
        {enrichedFiles.length >= 1 && (
          <div className="cont-files">
            {insertHtml}
            {enrichedFiles?.map((file: any, index) => (
              <InputUploadedFile
                key={file.queueId || file.key || index}
                file={file}
                deleteFile={!file.state || file.state === 'draft'? deleteFile: undefined}
                isActionSeeFile={(file.state && file.state !== 'draft') || file.key}
                icon={uploadCircleSvg}
                state={file.state}
                loadingDeleteFile={loading}
                uploadStatus={file.uploadStatus}
                errorMessage={file.errorMessage}
                onRetry={file.onRetry}
              />
            ))}
          </div>
        )}
      </div>
      {button}
    </div>
  );
};
export default UploadDocs;
